"use client";

import { useState } from "react";
import Link from "next/link";
import { MailCheck, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { requestPasswordReset } from "@/lib/auth/client";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    const result = await requestPasswordReset({ email });
    if (!result.ok) {
      setError(result.error ?? "Unable to request password reset");
      setLoading(false);
      return;
    }
    setSent(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white/90 p-8 shadow-lg">
        <div className="mb-6 flex items-center gap-2 text-emerald-600">
          <MailCheck className="h-5 w-5" />
          <span className="text-sm font-semibold uppercase tracking-[0.25em]">Password reset</span>
        </div>

        <h1 className="text-2xl font-bold text-slate-900">Forgot your password?</h1>
        <p className="mt-2 text-sm text-slate-500">
          Enter your account email and we will send a secure reset link.
        </p>

        {sent ? (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
            If an account exists for <span className="font-semibold">{email}</span>, a reset link has been sent.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Email address</label>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>

            {error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">
                {error}
              </div>
            )}

            <Button type="submit" variant="accent" size="lg" className="w-full rounded-xl" disabled={loading}>
              {loading ? "Sending..." : <>Send reset link <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-slate-500">
          Remembered your password?{" "}
          <Link href="/auth/login" className="font-semibold text-emerald-600 hover:text-emerald-700">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
